/**
 * TemplatePickerDialog – select a meal template to apply to a meal category.
 * Templates are ranked by pantry match score (highest first).
 * A live search field filters by template name.
 * Each ingredient badge is coloured:
 *   green  = enough in pantry
 *   yellow = partial (some in pantry, not enough)
 *   red/gray = missing from pantry
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { BookOpen, CheckCircle2, Search } from "lucide-react";

interface TemplatePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  klientId: number;
  datum: string;
  prijemKategoriaId: number;
  prijemKategoriaNazov: string;
  /** Called after a template has been successfully applied */
  onApplied: () => void;
}

export function TemplatePickerDialog({
  open,
  onOpenChange,
  klientId,
  datum,
  prijemKategoriaId,
  prijemKategoriaNazov,
  onApplied,
}: TemplatePickerDialogProps) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [applying, setApplying] = useState(false);
  const [search, setSearch] = useState("");

  const { data: templates = [], isLoading } = trpc.sablona.list.useQuery(
    { klientId },
    { enabled: open }
  );

  // Live-filter by search term (case-insensitive, matches name or notes)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      tmpl =>
        tmpl.nazov.toLowerCase().includes(q) ||
        (tmpl.poznamka ?? "").toLowerCase().includes(q)
    );
  }, [templates, search]);

  const applyMutation = trpc.prijem.applyTemplate.useMutation();
  const utils = trpc.useUtils();

  const handleApply = async () => {
    if (!selectedId) return;
    setApplying(true);
    try {
      await applyMutation.mutateAsync({
        sablonaId: selectedId,
        klientId,
        datum,
        prijemKategoriaId,
      });
      toast.success(t("mealLibrary:sablona.applied", "Template applied"));
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      onApplied();
      onOpenChange(false);
      setSelectedId(null);
      setSearch("");
    } catch {
      toast.error(
        t("mealLibrary:sablona.applyError", "Failed to apply template")
      );
    } finally {
      setApplying(false);
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setSelectedId(null);
      setSearch("");
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/*
        Keep DialogContent at its default grid layout (don't add flex/flex-col).
        We widen it to max-w-2xl so ingredient badges have room to wrap.
        The ScrollArea gets an explicit max-h so it scrolls independently;
        the footer always stays below it in the natural grid flow.
      */}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <BookOpen className="h-5 w-5 shrink-0" />
            <span>
              {t("mealLibrary:sablona.pickTitle", "Apply Meal Template")}
            </span>
            <span className="text-sm font-normal text-muted-foreground">
              → {prijemKategoriaNazov}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Live search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9"
            placeholder={t(
              "mealLibrary:sablona.searchPlaceholder",
              "Search templates…"
            )}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              // Clear selection if it is no longer in filtered results
              if (
                selectedId &&
                !filtered.some(tmpl => tmpl.id === selectedId)
              ) {
                setSelectedId(null);
              }
            }}
          />
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4">
            {t("common:loading", "Loading…")}
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 italic">
            {search
              ? t(
                  "mealLibrary:sablona.noSearchResults",
                  "No templates match your search."
                )
              : t(
                  "mealLibrary:sablona.noTemplates",
                  "No meal templates found."
                )}
          </p>
        ) : (
          /* Explicit height so ScrollArea actually scrolls */
          <ScrollArea className="h-[min(420px,55vh)]">
            <div className="space-y-2 pr-3">
              {filtered.map(tmpl => {
                const pct = Math.round(tmpl.matchScore * 100);
                const isSelected = selectedId === tmpl.id;

                return (
                  <button
                    key={tmpl.id}
                    onClick={() => setSelectedId(isSelected ? null : tmpl.id)}
                    className={[
                      "w-full text-left rounded-lg border p-3 transition-colors",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-muted/40",
                    ].join(" ")}
                  >
                    {/* Row 1: name (left) + pantry score (right) */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {isSelected && (
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        )}
                        <span className="font-medium truncate">
                          {tmpl.nazov}
                        </span>
                      </div>

                      {/* Pantry match score – fixed width, never truncated */}
                      {tmpl.totalCount > 0 && (
                        <div className="shrink-0 w-24 text-right">
                          <span
                            className={[
                              "text-xs font-semibold",
                              pct === 100
                                ? "text-green-600"
                                : pct >= 50
                                  ? "text-yellow-600"
                                  : "text-muted-foreground",
                            ].join(" ")}
                          >
                            {pct}%
                          </span>
                          <Progress value={pct} className="h-1.5 mt-1" />
                          <span className="text-[10px] text-muted-foreground">
                            {tmpl.matchCount}/{tmpl.totalCount}{" "}
                            {t("mealLibrary:sablona.inPantry", "in pantry")}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Row 2: optional notes */}
                    {tmpl.poznamka && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {tmpl.poznamka}
                      </p>
                    )}

                    {/* Row 3: ingredient badges – full width, free to wrap */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {tmpl.items.map(item => {
                        const status =
                          (
                            (tmpl as any).itemStatus as
                              | Record<number, "green" | "yellow" | "red">
                              | undefined
                          )?.[item.id] ?? "red";
                        const cls =
                          status === "green"
                            ? "border-green-400 bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300"
                            : status === "yellow"
                              ? "border-yellow-400 bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300"
                              : "border-border text-muted-foreground";
                        const tooltip =
                          status === "green"
                            ? "Dostupné v špajzi"
                            : status === "yellow"
                              ? "Čiastočne dostupné"
                              : "Chýba v špajzi";
                        return (
                          <Badge
                            key={item.id}
                            variant="outline"
                            className={`text-xs border ${cls}`}
                            title={tooltip}
                          >
                            {item.potravina.nazov}
                            {item.hmotnost && (
                              <span className="ml-1 opacity-70">
                                {item.hmotnost}g
                              </span>
                            )}
                          </Badge>
                        );
                      })}
                      {tmpl.items.length === 0 && (
                        <span className="text-xs text-muted-foreground italic">
                          {t("mealLibrary:sablona.noItems", "No items")}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common:cancel", "Cancel")}
          </Button>
          <Button onClick={handleApply} disabled={!selectedId || applying}>
            {applying
              ? t("mealLibrary:sablona.applying", "Applying…")
              : t("mealLibrary:sablona.apply", "Apply Template")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
